import * as THREE from "three";
import { isWallType, RulerLineType, SingleLineType, WallType } from "../../types/floorplannerTypes";
import { floorplannerStore } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";

export const handleWidth = 0.08;
export const handleSize = 0.08;
export const tubeRadius = 0.02;
/** Smallest mid-handle size still worth rendering on very short walls/lines. */
export const MIN_MIDDLE_HANDLE_SIZE = 0.02;
// Keep single-line edit handles above the multi-selection plane at z=0.021
// so handle drags win hit testing over group/body drag targets.
export const singleLineHandleZ = 0.03;

export const endpointHandleHitSize = () => (handleWidth * 2) / editorStore.zoomLevelDivisor();

/**
 * Preferred gap between the center control handle and each length mid-handle.
 * Mid-handle centers are placed at `size + gap` from the wall/line midpoint.
 */
export const wallMidHandleGap = () => 0.09 / editorStore.zoomLevelDivisor();

/**
 * Preferred mid-handle size for walls (before length clamping).
 * Matches the previous WallShape sizing so normal walls keep the same look.
 */
export const getPreferredWallMiddleHandleSize = (wallWidth: number): number => {
  const zoomMin =
    editorStore.zoomLevel < 3
      ? Math.max(0.15, 0.15 / editorStore.zoomLevelDivisor())
      : 0.1;
  return Math.max(zoomMin, wallWidth);
};

/**
 * Preferred mid-handle size for single lines (before length clamping).
 */
export const getPreferredSingleLineMiddleHandleSize = (): number => {
  if (editorStore.zoomLevel < 3) {
    return Math.max(0.15, 0.15 / editorStore.zoomLevelDivisor(), 0.1);
  }
  return Math.max(0.025, 0.3 / Math.max(editorStore.zoomLevel, 0.001));
};

/**
 * Shrink mid-handles on short walls/lines so they stay clear of the endpoint
 * free-move rings. Without this, walls under ~320mm lose free diagonal moves
 * because the square mid-handles cover the rings.
 *
 * Geometry: mid-handle center is at `size + gap` from midpoint; outer edge at
 * `1.5 * size + gap`. Keep that edge inside half-length minus endpoint hit radius.
 */
export const getMiddleHandleSizeForLength = (
  length: number,
  preferredSize: number,
): number => {
  const gap = wallMidHandleGap();
  const endpointClearance = endpointHandleHitSize() * 0.5;
  const availableAlongHalf = length / 2 - endpointClearance - gap;
  if (availableAlongHalf <= 0) {
    return Math.min(preferredSize, Math.max(MIN_MIDDLE_HANDLE_SIZE, length * 0.2));
  }
  const maxSize = availableAlongHalf / 1.5;
  return Math.min(preferredSize, Math.max(MIN_MIDDLE_HANDLE_SIZE, maxSize));
};

/**
 * Offset from wall/line midpoint to a mid-handle center, clamped so the handle
 * does not cover the endpoint free-move ring.
 */
export const getMiddleHandleOffset = (
  length: number,
  handleSizeValue: number,
): number => {
  const preferredOffset = handleSizeValue + wallMidHandleGap();
  const endpointClearance = endpointHandleHitSize() * 0.5;
  const maxOffset = Math.max(
    handleSizeValue / 2,
    length / 2 - endpointClearance - handleSizeValue / 2,
  );
  return Math.min(preferredOffset, Math.max(0, maxOffset));
};

export const createMiddleDragHandle = (wall: WallType | SingleLineType | RulerLineType, wallWidth: number) => {
  const shape = new THREE.Shape();
  // Use exactly the passed wallWidth - the caller is responsible for applying any minimums
  const height = wallWidth;
  const width = height;
  shape.moveTo(-width / 2, -height / 2);
  shape.lineTo(width / 2, -height / 2);
  shape.lineTo(width / 2, height / 2);
  shape.lineTo(-width / 2, height / 2);
  shape.lineTo(-width / 2, -height / 2);

  const hole = new THREE.Path();
  const holeWidth = width - (0.02 / editorStore.zoomLevelDivisor());
  const holeHeight = height - (0.02 / editorStore.zoomLevelDivisor());
  hole.moveTo(-holeWidth / 2, -holeHeight / 2);
  hole.lineTo(holeWidth / 2, -holeHeight / 2);
  hole.lineTo(holeWidth / 2, holeHeight / 2);
  hole.lineTo(-holeWidth / 2, holeHeight / 2);
  hole.lineTo(-holeWidth / 2, -holeHeight / 2);

  shape.holes.push(hole);

  return new THREE.ExtrudeGeometry(shape, {
    depth: 0.01,
    bevelEnabled: false,
  });
};
